'use client'

import React from 'react'
import { cva } from 'class-variance-authority'
import type { VariantProps } from 'class-variance-authority'
import { Attention, Check, Info } from '@/icons'
import { cn } from '@/utils'

export const helperTextVariants = cva(
  ['flex', 'items-center', 'gap-1', 'text-xs', 'leading-4', 'text-white'],
  {
    variants: {
      /**
       * Message tone, which picks both the text colour and the default icon:
       * `positive`, `attention`, `danger`, `support` or `neutral`. Defaults to
       * `neutral`.
       */
      variant: {
        positive: 'text-positive-300',
        attention: 'text-attention-400',
        danger: 'text-danger-300',
        support: 'text-support-400',
        neutral: 'text-gray-600',
      },

      /**
       * Text and icon scale: `sm` 12px text, `md` 14px, `lg` 16px, each with a
       * matching icon badge and gap. Defaults to `md`.
       */
      size: {
        sm: 'text-2xs gap-1',
        md: 'text-xs gap-1.5',
        lg: 'text-sm gap-2',
      },
    },
    defaultVariants: {
      variant: 'neutral',
      size: 'md',
    },
  },
)

export const iconSizeVariants = cva(
  'rounded-full flex items-center justify-center',
  {
    variants: {
      size: {
        sm: 'size-4 [&>svg]:size-2',
        md: 'size-5 [&>svg]:size-3',
        lg: 'size-6 [&>svg]:size-3',
      },
      variant: {
        positive: 'bg-positive-200 [&>svg]:fill-white',
        attention: 'bg-attention-300 [&>svg]:fill-white',
        danger: 'bg-danger-200 [&>svg]:fill-white',
        support: 'bg-support-200 [&>svg]:fill-white',
        neutral: 'bg-gray-300 [&>svg]:fill-gray-500',
      },
    },
    defaultVariants: {
      size: 'md',
      variant: 'neutral',
    },
  },
)

/**
 * Props for `HelperText`. On top of its own props it accepts the standard HTML
 * attributes, which are spread onto the root element — a `<small>`, not a
 * `<div>`.
 */
export type HelperTextProps = VariantProps<typeof helperTextVariants> &
  React.HTMLAttributes<HTMLSpanElement> & {
    /**
     * The content to be displayed in the helper text
     */
    children: React.ReactNode

    /**
     * Custom icon to override the default icon based on variant
     */
    icon?: React.ReactNode
  }

/**
 * HelperText is a component that displays contextual information
 * with an icon and text, supporting different variants for different
 * types of messages (positive, attention, danger, support, neutral).
 */
export const HelperText = ({
  className,
  variant = 'neutral',
  size = 'md',
  children,
  icon,
  ...props
}: HelperTextProps) => {
  const getIcon = () => {
    // Return custom icon if provided
    if (icon) {
      return icon
    }

    // Return default icon based on variant
    switch (variant) {
      case 'positive':
        return <Check />
      case 'attention':
      case 'danger':
        return <Attention />
      case 'support':
      case 'neutral':
      default:
        return <Info />
    }
  }

  return (
    <small
      data-testid="helper-text"
      className={cn(helperTextVariants({ variant, size, className }))}
      {...props}
    >
      <span className={iconSizeVariants({ size, variant })}>{getIcon()}</span>
      <span className="text-inherit">{children}</span>
    </small>
  )
}
